import React, { useEffect, useState } from 'react';
import { isViewEnabled } from './features';
import './App.css';
import './StockInward.css';
import StockInward from './StockInward';
import Layout from './components/Layout';
import ItemMaster from './pages/ItemMaster';
import SupplierMaster from './pages/SupplierMaster';
import SalesEntry from './pages/SalesEntry';
import Billing from './pages/Billing';
import BillingReport from './pages/BillingReport';
import BillingSummaryReport from './pages/BillingSummaryReport';
import MarketReports from './pages/MarketReports';
import PurchaseReports from './pages/PurchaseReports';
import Dashboard from './pages/Dashboard';
import CustomerMaster from './pages/CustomerMaster';
import StoreMaster from './pages/StoreMaster';
import ExpenseMaster from './pages/ExpenseMaster';
import EmployeeAttendance from './pages/EmployeeAttendance';
import EmployeeMaster from './pages/EmployeeMaster';
import AmountReceived from './pages/AmountReceived';
import MiscExpenses from './pages/MiscExpenses';
import Login from './pages/Login';
import UserAccess from './pages/UserAccess';
import BillRequests from './pages/BillRequests';
import { getAuthToken, getCurrentUser, login, logout, setAuthToken, setUnauthorizedHandler } from './storage';

const EXPENSE_VIEWS = ['expenseDelivery', 'expenseStall', 'expenseMarket', 'expenseMaster'];
const LEGACY_EXPENSE_VIEW = 'miscExpenses';

const VIEW_ORDER = [
  'dashboard',
  'amountReceived',
  'inward',
  'purchaseReports',
  'billing',
  'billingReport',
  'billingSummary',
  ...EXPENSE_VIEWS,
  'salesStall',
  'stallReports',
  'salesMarket',
  'marketReports',
  'itemMaster',
  'supplierMaster',
  'customerMaster',
  'expenseNameMaster',
  'employeeMaster',
  'employeeAttendance',
  'userAccess',
  'billRequests',
];

function canAccessView(user, view) {
  if (!isViewEnabled(view)) return false;
  if (view === 'noAccess') return true;
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (view === 'storeMaster') return false;
  const allowed = Array.isArray(user.allowedViews) ? user.allowedViews : [];
  if (EXPENSE_VIEWS.includes(view)) {
    return allowed.includes(view) || allowed.includes(LEGACY_EXPENSE_VIEW);
  }
  return allowed.includes(view === 'billingSummary' ? 'billingReport' : view);
}

function firstAllowedView(user) {
  if (!user) return 'inward';
  if (user.role === 'admin') return 'dashboard';
  const match = VIEW_ORDER.find((v) => canAccessView(user, v));
  return match || 'noAccess';
}

function App() {
  const [view, setView] = useState('inward');
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthToken("");
      setUser(null);
      setView('inward');
    });

    const token = getAuthToken();
    if (!token) {
      setCheckingAuth(false);
      return;
    }

    getCurrentUser()
      .then((data) => {
        setUser(data);
        if (data) {
          setView(firstAllowedView(data));
        }
      })
      .catch(() => setAuthToken(""))
      .finally(() => setCheckingAuth(false));
  }, []);

  const handleLogin = async (username, password) => {
    const data = await login(username, password);
    if (data && data.user) {
      setUser(data.user);
      setView(firstAllowedView(data.user));
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      setUser(null);
      setView('inward');
    }
  };

  useEffect(() => {
    if (!user) return;
    if (canAccessView(user, view)) return;
    setView(firstAllowedView(user));
  }, [user, view]);

  const handleNavigate = (nextView) => {
    if (canAccessView(user, nextView)) {
      setView(nextView);
      return;
    }
    alert("You do not have access to this page.");
  };

  if (checkingAuth) {
    return <div className="App"><div className="loading">Loading...</div></div>;
  }

  if (!user) {
    return (
      <div className="App">
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  return (
    <div className="App">
      <Layout currentView={view} onNavigate={handleNavigate} currentUser={user} onLogout={handleLogout}>
        {view === 'noAccess' && (
          <div className="stock-inward-container" style={{ paddingTop: 0 }}>
            <div className="form-card">
              <div className="card-header">
                <h2 className="card-title">No Access</h2>
              </div>
              <div className="inward-form">
                <div className="empty-state" style={{ padding: "16px" }}>
                  Please contact admin to enable page access for your account.
                </div>
              </div>
            </div>
          </div>
        )}
        {view === 'dashboard' && <Dashboard />}
        {view === 'amountReceived' && <AmountReceived currentUser={user} />}
        {view === 'inward' && <StockInward currentUser={user} />}
        {view === 'purchaseReports' && <PurchaseReports />}
        {view === 'inwardStall' && <StockInward category="Stall" title="Stall - Inward & Sales" currentUser={user} />}
        {view === 'inwardMarket' && <StockInward category="Market" title="Market - Material Inward" currentUser={user} />}
        {view === 'itemMaster' && <ItemMaster />}
        {view === 'supplierMaster' && <SupplierMaster />}
        {view === 'customerMaster' && <CustomerMaster />}
        {view === 'storeMaster' && <StoreMaster />}
        {view === 'expenseNameMaster' && <ExpenseMaster />}
        {view === 'employeeMaster' && <EmployeeMaster />}
        {view === 'employeeAttendance' && <EmployeeAttendance currentUser={user} />}
        {view === 'salesStall' && <Billing key="stall" category="Stall" currentUser={user} />}
        {view === 'stallReports' && <MarketReports category="Stall" title="Stall Reports" />}
        {view === 'salesMarket' && <SalesEntry category="Market" currentUser={user} />}
        {view === 'marketReports' && <MarketReports />}
        {view === 'billing' && <Billing key="delivery" currentUser={user} />}
        {view === 'billingReport' && <BillingReport currentUser={user} />}
        {view === 'billingSummary' && <BillingSummaryReport />}
        {(view === 'expenseDelivery' || view === 'miscExpenses') && (
          <MiscExpenses category="Delivery" title="Delivery Expenses" />
        )}
        {view === 'expenseStall' && <MiscExpenses category="Stall" title="Stall Expenses" />}
        {view === 'expenseMarket' && <MiscExpenses category="Market" title="Market Expenses" />}
        {view === 'expenseMaster' && <MiscExpenses category="Master" title="Master Expense" />}
        {view === 'userAccess' && <UserAccess currentUser={user} />}
        {view === 'billRequests' && <BillRequests currentUser={user} />}
      </Layout>
    </div>
  );
}

export default App;
